import React, { useEffect, useRef, useState } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';

const SIZE = 256; // square output (and preview) in px

interface Props {
  file: File;
  busy?: boolean;
  onCancel: () => void;
  /** Cropped square PNG blob, ready to upload. */
  onCrop: (blob: Blob) => void;
}

/**
 * Minimal square logo cropper — no third-party library. Loads the picked image, lets the
 * user zoom + drag to frame it inside a square, and exports a SIZE×SIZE PNG. Upload-and-crop
 * only (no AI generation).
 */
const LogoCropper: React.FC<Props> = ({ file, busy, onCancel, onCrop }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { setImg(image); setZoom(1); setOffset({ x: 0, y: 0 }); };
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Re-draw whenever the image, zoom, or pan changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const base = Math.max(SIZE / img.width, SIZE / img.height); // cover
    const scale = base * zoom;
    const w = img.width * scale, h = img.height * scale;
    // Clamp pan so the square stays fully covered.
    const maxX = Math.max(0, (w - SIZE) / 2), maxY = Math.max(0, (h - SIZE) / 2);
    const ox = Math.max(-maxX, Math.min(maxX, offset.x));
    const oy = Math.max(-maxY, Math.min(maxY, offset.y));
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.drawImage(img, (SIZE - w) / 2 + ox, (SIZE - h) / 2 + oy, w, h);
  }, [img, zoom, offset]);

  const onPointerDown = (e: React.PointerEvent) => { drag.current = { x: e.clientX, y: e.clientY }; };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    setOffset((p) => ({ x: p.x + dx, y: p.y + dy }));
  };
  const onPointerUp = () => { drag.current = null; };

  const confirm = () => {
    canvasRef.current?.toBlob((blob) => { if (blob) onCrop(blob); }, 'image/png');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div className="w-full max-w-xs rounded-2xl bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-600 shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Crop logo</h2>
          <button onClick={onCancel} aria-label="Cancel" title="Cancel" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-col items-center gap-3">
          <canvas
            ref={canvasRef}
            width={SIZE}
            height={SIZE}
            data-testid="logo-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            className="w-56 h-56 rounded-full bg-secondary-100 dark:bg-secondary-800 cursor-move touch-none"
          />
          <input type="range" min={1} max={3} step={0.01} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Zoom" title="Zoom" className="w-full" />
          <button onClick={confirm} disabled={busy || !img}
            className={`w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2 ${TYPE.body} font-medium text-white dark:text-secondary-900 disabled:opacity-40`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Use this logo
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoCropper;
